import { Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ModalController, NavController } from '@ionic/angular';
import { UserInfoProfile, UserInfoService } from '@wlp/ui-bs-login';
import { PaymentPixService } from '@wlp/ui-bs-pix';
import { UiBsTranslateService } from '@wlp/ui-bs-translate';
import { KeyTypeEnum } from '@wlp/ui-px-pix';
import { LayoutConfigModel } from 'src/app/core/layout/models/layout-config.model';
import { LayoutConfigService } from 'src/app/core/layout/services/layout-config.service';
import { TransactionStoreAction } from 'src/app/core/store/pix/actions/transaction.store.action';
import { LoadingService } from '../../../../../../../core/layout/services/loading.service';
import { IGoToPrepareTransactionDTO } from '../../../../../../../core/store/pix/models/transaction.store.model';
import { ModalAlertComponent } from '../../../../modals/modal-alert/modal-alert.component';

@Component({
  selector: 'app-key-transaction',
  templateUrl: './key-transaction.page.html',
  styleUrls: ['./key-transaction.page.scss'],
})
export class KeyTransactionPage implements OnInit {
  private langDefault: string;
  private layoutConfigValue: LayoutConfigModel;
  private userProfile: UserInfoProfile;
  public formCpfCnpj: FormGroup;
  public formPhone: FormGroup;
  public formEmail: FormGroup;
  public formRandomKey: FormGroup;
  public errorMessages: any;
  public switchSelectedKey = 'cpf-cnpj';

  constructor(
    private layoutConfigService: LayoutConfigService,
    private translate: UiBsTranslateService,
    private router: Router,
    private navCtrl: NavController,
    private loadingService: LoadingService,
    private transactionStoreAction: TransactionStoreAction,
    protected userInfoService: UserInfoService,
    private paymentPixService: PaymentPixService,
    public modalController: ModalController,
  ) {}

  public ngOnInit() {
    this.layoutConfigValue = this.layoutConfigService.getSavedConfig();
    this.langDefault = this.layoutConfigValue.wlTheme.languageDefault;
    this.translate.setDefaultLang(this.langDefault);

    this.loadUser();
    this.createForms();
  }

  public async createForms() {
    const forms = this.paymentPixService.createForms();
    this.formCpfCnpj = forms.formCpfCnpj;
    this.formPhone = forms.formPhone;
    this.formEmail = forms.formEmail;
    this.formRandomKey = forms.formRandomKey;
    this.errorMessages = this.paymentPixService.getErrorMessages();
  }

  private loadUser() {
    try {
      const userDetails = this.userInfoService.getInfoProfile();
      userDetails.then((user) => {
        this.userProfile = user;
      });
    } catch (e) {}
  }

  public segmentChanged() {
    this.formCpfCnpj.reset();
    this.formPhone.reset();
    this.formEmail.reset();
    this.formRandomKey.reset();
  }

  public formIsValid(): boolean {
    const formMap = {
      [KeyTypeEnum.CPF_CNPJ]: this.formCpfCnpj,
      [KeyTypeEnum.PHONE]: this.formPhone,
      [KeyTypeEnum.EMAIL]: this.formEmail,
      [KeyTypeEnum.RANDOM_KEY]: this.formRandomKey,
    };

    const selectedForm = formMap[this.switchSelectedKey as KeyType];
    return selectedForm ? selectedForm.valid : false;
  }

  public async continue(selectedKey: string, form: FormGroup) {
    this.loadingService.show();

    const keyFormat = this.getFormattedKey(selectedKey, form);
    if (!keyFormat) {
      this.loadingService.hide();
      return;
    }

    this.paymentPixService
      .paymentByKey(this.userProfile.uuidAccount, keyFormat)
      .subscribe(
        (res) => {
          if(res.statusCode === 200) {
            this.navigateToPrepareTransaction(res);
          }

          if (res.statusCode === 400) {
            this.modalNotification('warning-circle', res.message);
          } else {
            this.modalNotification('warning-circle', 'Não foi possível buscar a chave. Tente novamente mais tarde.');
          }
        },
        (err) => {
          console.error(err);
          this.modalNotification('warning-circle', 'Houve um erro inexperado. Tente novamente mais tarde.');
          this.loadingService.hide();
        },
        () => {
          this.loadingService.hide();
        },
      );
  }

  public navigateByUrl(url: string, replaceUrl: boolean = true): void {
    this.router.navigate([url], { replaceUrl });
  }

  private navigateToPrepareTransaction(payload: any): void {
    const response: IGoToPrepareTransactionDTO = {
      payload,
      isFavorite: false,
      userProfile: this.userProfile,
      links: {
        back: '/app/dashboard',
        go: '/app/pix/prepare-transaction',
      },
    };

    this.transactionStoreAction.update('setPrepareTransactionByKeyPix', {
      payload: { ...response.payload },
      pageData: {
        isFavorite: response.isFavorite,
        userProfile: response.userProfile,
        links: {
          back: `/app/pix/${response.links.back}`,
          go: `/app/pix/${response.links.go}`,
        },
      },
    });

    this.navigateByUrl(response.links.go, true);
  }

  private getFormattedKey(
    selectedKey: string,
    formKey: FormGroup,
  ): string | undefined {
    let key: string;
    switch (selectedKey) {
      case KeyTypeEnum.CPF_CNPJ:
        key = this.removeNonDigits(formKey.controls.document.value);
        break;
      case KeyTypeEnum.PHONE:
        key = `+55${this.removeNonDigits(formKey.controls.phone.value)}`;
        break;
      case KeyTypeEnum.EMAIL:
        key = formKey.controls.email.value;
        break;
      case KeyTypeEnum.RANDOM_KEY:
        key = formKey.controls.evp.value;
        break;
      default:
        return undefined;
    }
    return key;
  }

  private removeNonDigits(input: string): string {
    return input.replace(/\D/g, '');
  }

  public goBack() {
    this.navCtrl.navigateRoot('/app/dashboard');
  }

  private async modalNotification(status: string, message: string, description?: string) {
    this.loadingService.hide();
    const modalSuccess = await this.modalController.create({
      component: ModalAlertComponent,
      cssClass: 'modal-alert-class',
      componentProps: {
        status,
        message,
        description,
        confirmBtn: 'Ok',
        translate: true,
      },
      backdropDismiss: false,
    });
    return await modalSuccess.present();
  }
}
